import React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'

import { theme, breakpoint } from '../styles'

import { SEO } from '../components'

import { MaxWidthWrapper } from '../components/layouts'

/* Containers  */
const WhatIsEMSPageContainer = styled.section`
  background: ${theme.color.solid.white};
  color: ${theme.color.solid.pureblk};
`

const Wrapper = styled(MaxWidthWrapper)`
  margin: 0 auto;
  padding: 147px 8rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    padding: 131px 7rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    padding: 67px 2.4rem;
  }
`
const ContentContainer = styled.div`
  width: 648px;
  max-width: 100%;
  margin: 0 auto;
  margin-bottom: 7.9rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    margin-bottom: 5rem;
  }
`

const WhatIsEMSAnchorOffset = styled.div`
  /* tablet header height  */
  height: 84px;
  margin-top: -84px;

  @media (max-width: ${breakpoint.largeTablet}) {
    height: 66px;
    margin-top: -82px;
  }
`

/* Headers */

const Header = styled.h2`
  text-align: center;
  margin-bottom: 7.7rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 3.8rem;
    margin-top: 0;
    margin-bottom: 11.8rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    font-size: 3.6rem;
    margin-bottom: 5rem;
  }
`

const Subheader = styled.h5`
  font-family: Oswald;
  font-weight: 700;
  font-size: 3rem;
  line-height: 3rem;
  text-transform: uppercase;

  margin-bottom: 1.9rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 2.4rem;
    line-height: 2.4rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    font-size: 1.8rem;
    line-height: 1.8rem;
  }
`

const SubSubheader = styled.h6`
  font-family: Oswald;
  font-weight: 700;
  font-size: 2.4rem;
  line-height: 3rem;
  text-transform: capitalize;

  margin-bottom: 1.9rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 1.8rem;
    line-height: 2.4rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    font-size: 1.6rem;
  }
`

const List = styled.ul``

const ListItem = styled.li`
  font-family: Lato;
  font-size: 1.8rem;
  line-height: 3rem;
  color: ${theme.color.solid.pureblk};
  margin-bottom: 2.4rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 1.4rem;
    line-height: 2.4rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    font-size: 1.2rem;
    line-height: 1.8rem;
  }
`

/* Paragraphs */

const Paragraph = styled.p`
  font-size: 1.8rem;
  line-height: 3rem;

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 1.4rem;
    line-height: 2.4rem;
  }

  @media (max-width: ${breakpoint.largeMobile}) {
    font-size: 1.2rem;
    line-height: 1.8rem;
  }
`

/* Links */

const InternalLink = styled(Link)`
  font-family: Lato;
  font-weight: 700;
  font-size: 1.8rem;
  line-height: 3rem;
  text-decoration: none;
  color: ${theme.color.solid.lgt_blue};

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 1.4rem;
    line-height: 2.4rem;
  }
`

const ExternalLink = styled.a`
  font-family: Lato;
  font-weight: 700;
  font-size: 1.8rem;
  line-height: 3rem;
  text-decoration: none;
  color: ${theme.color.solid.lgt_blue};

  @media (max-width: ${breakpoint.largeTablet}) {
    font-size: 1.4rem;
    line-height: 2.4rem;
  }
`

const WhatIsEMSPage = () => (
  <>
    <SEO title="WhatIsEMS" />
    <WhatIsEMSPageContainer>
      <Wrapper>
        <Header>ABOUT EMS</Header>

        <ContentContainer>
          <Subheader>Scientific Background</Subheader>
          <Paragraph>
            Before we talk specifically about the science of Electro Muscle
            Stimulation (EMS), let’s lay some groundwork. Of course, you can
            jump ahead to&nbsp;
            <InternalLink to="/about-ems#ems">“What is EMS?”</InternalLink>.
          </Paragraph>

          <Paragraph>
            The human body is composed of various organ systems, each of which
            have their own purpose and functions. The organ system we will focus
            on here is the muscular system. Your body has three categories of
            muscles: visceral, cardiac and skeletal.
          </Paragraph>

          <List>
            <ListItem>
              Visceral muscles include the digestive system, bladder and, in
              females, the uterus. These are involuntary muscles, meaning that
              they more or less work without you thinking about it and cannot be
              engaged on demand.
            </ListItem>
            <ListItem>
              Cardiac muscles, as the name suggests, are only found in the
              heart. Like visceral muscles, the cardiac muscles are involuntary
              and operate without you thinking about it.
            </ListItem>
            <ListItem>
              Skeletal muscles are the muscles we see and feel when we flex, and
              they are the muscles we exercise during physical activity. These
              muscles are attached to the skeleton and come in pairs, one muscle
              to move the bone in a particular direction, and the other to move
              it back. Skeletal muscles are voluntary, meaning that you have to
              think about engaging the muscle in order for your nervous system
              to tell it to activate. In fact, these are the only muscles that
              can be activated by choice (whether through your own signal, or
              with the help of EMS).
            </ListItem>
          </List>

          <Paragraph>
            The human body has more than 650 known skeletal muscles, each made
            up of muscle fibers, or muscle cells. Your body has two types of
            muscle fibers: slow-twitch and fast-twitch.
          </Paragraph>

          <List>
            <ListItem>
              Slow-twitch muscle fibers are used for longer-distance, endurance
              activities such as running a marathon. These are more energy
              efficient and hence the “preferred” muscle fiber for the body to
              use in everyday activities. They are also easier to train.
            </ListItem>
            <ListItem>
              Fast-twitch muscle fibers are used for quick, powerful movements.
              They can only be trained with maximum loads and fast movements.
              Contrary to their name, they actually kick in later during a
              contraction, but react faster, thus creating a large force.
              Fast-twitch muscle fibers are used for intense athletic
              activities, such as sprinting or powerlifting, but also for
              quickly reacting if you trip and need to prevent a fall.
            </ListItem>
          </List>

          <Paragraph>
            When you engage in physical activity, whether lifting weights or
            unloading groceries from the car, your skeletal muscles contract.
            Each contraction begins with a signal that is sent from your brain
            to your motor nerves. This signal then forces your muscles to
            contract in order to move the object. When using EMS, the motor
            nerve receives the signal from the device instead of the brain, and
            the muscle cannot tell the difference. This is why it is possible to
            train muscles using EMS.
          </Paragraph>
        </ContentContainer>

        <ContentContainer>
          <SubSubheader>How to build muscle</SubSubheader>
          <Paragraph>
            Growing muscle mass requires placing increasing amounts of stress on
            your muscles, resulting in an adaptation process to be better
            prepared in the future. This stress results in a process that
            facilitates muscle growth: muscle tension, muscle damage and
            metabolic stress.
          </Paragraph>

          <List>
            <ListItem>
              Muscle tension is the act of lifting progressively heavier weights
              than your body has previously adapted to. Breaking through this
              threshold causes chemical changes in your muscles as they adapt to
              the increased stress over time.
            </ListItem>
            <ListItem>
              Muscle damage is the soreness you feel after working out. This
              muscle damage releases inflammatory molecules and immune systems
              cells that cause muscle cells to jump into action.
            </ListItem>
            <ListItem>
              Metabolic stress causes swelling around the muscle and connective
              tissue. This type of growth is known as sarcoplasmic hypertrophy.
            </ListItem>
          </List>

          <Paragraph>
            During a conventional workout, you can get less than half of your
            muscle fibers to contract. This is terribly inefficient, especially
            given the stress and strain on your joints and tendons. As you will
            see below, EMS engages more than 2x the muscle fibers you can on
            your own.
          </Paragraph>
        </ContentContainer>

        <ContentContainer>
          <SubSubheader>How to lose muscle</SubSubheader>
          <Paragraph>
            Just as muscles can grow, they can also atrophy, or waste away.
            Atrophy is primarily caused by a lack of use of the muscle.
            Sometimes the lack of activity is due to an injury or illness, when
            it is more difficult to create muscle tension to maintain strength
            and healthy muscle mass.
          </Paragraph>

          <Paragraph>
            Aging is another leading cause of muscle atrophy due to a decline in
            physical exercise, because your body produces fewer proteins that
            promote muscle growth, causing muscles to shrink (a condition called
            sarcopenia). The Mayo Clinic released&nbsp;
            <ExternalLink
              href="https://www.mayoclinic.org/healthy-lifestyle/healthy-aging/in-depth/aging/art-20046070"
              target="_blank"
            >
              Ten Aspects of Healthy Aging
            </ExternalLink>
            , what to expect and how to slow the decline. For six of the ten
            aspects, physical exercise was listed as a top priority, but this
            becomes more difficult and risky as we age.
          </Paragraph>

          <Paragraph>
            With simple body weight movements and no weights or other equipment
            to cause impact on your joints and tendons, EMS is a safe and
            effective tool for physical exercise through every stage of life.
          </Paragraph>
        </ContentContainer>

        <WhatIsEMSAnchorOffset id="ems" />
        <ContentContainer>
          <Subheader>What is EMS?</Subheader>
          <Paragraph>
            Electro Muscle Stimulation (EMS) replicates the brain signals that
            command your muscles to contract. Usually composed of a control unit
            that generates the impulses and one or more electrodes mapped
            directly to your muscle(s), EMS technology sends low-frequency
            impulses that mimic your natural brain signals to directly engage
            your muscle(s). This results in a muscle contraction. Sophisticated
            EMS devices vary the impulse characteristics for each muscle group,
            and vary the frequencies depending on the desired training effect,
            such as strength, power, cardio and recovery.
          </Paragraph>

          <Paragraph>
            EMS technology has been around since the 1960s and goes by many
            names. You may have heard of Neuromuscular Electrical Stimulation
            (NEMS), e-stim, stim, powered muscle stimulators, or perhaps brands
            such as&nbsp;
            <ExternalLink href="https://www.compex.com/" target="_blank">
              Compex
            </ExternalLink>
            &nbsp;and&nbsp;
            <ExternalLink href="https://www.powerdot.com/" target="_blank">
              PowerDot
            </ExternalLink>
            . All of these are based on the same science and are used in a
            variety of contexts, from injury rehabilitation to localized
            strength training. In the U.S., EMS technology is regulated by the
            FDA as a Class II Medical Device.
          </Paragraph>

          <Paragraph>
            You may have heard of TENS units and wonder if they are the same
            thing. The answer is ‘not exactly’. While TENS units and EMS devices
            are similar in concept, TENS units are primarily designed to
            stimulate nerves for pain treatment rather than muscles for training
            and rehabilitation purposes.
          </Paragraph>
        </ContentContainer>

        <ContentContainer>
          <Subheader>What is Full-Body EMS?</Subheader>
          <Paragraph>
            Full-Body EMS takes this science a step further, with a wearable
            “suit” that maps electrodes to each of your major muscle groups. The
            low-frequency impulses that serve as the basis for EMS technology
            actively engage each muscle group, simultaneously. In just
            20-minutes, you can get a full-body workout that engages &gt;90% of
            the muscle fibers in your body.
          </Paragraph>

          <Paragraph>
            In addition training the whole body in just 20 minutes, Full-Body
            EMS also allows for the training of compound movements such as golf
            or baseball swings, jumping, sprinting, dribbling, or everyday
            functional exercises.
          </Paragraph>

          <Paragraph>
            The benefits of Full-Body EMS are striking. Studies have shown the
            training to increase strength, endurance and agility in users, while
            decreasing back pain, body tension and depression. In
            addition,&nbsp;
            <ExternalLink
              href="https://www.researchgate.net/publication/303918626_Whole-body_electromyostimulation_to_fight_sarcopenic_obesity_in_community-dwelling_older_women_at_risk_Resultsof_the_randomized_controlled_FORMOsA-sarcopenic_obesity_study"
              target="_blank"
            >
              medical studies
            </ExternalLink>
            &nbsp;have shown users to gain lean muscle mass, improve physique
            and even increase bone density. Full-Body EMS is also known to
            strengthen the pelvic floor and reduce postpartum incontinence in
            women.
          </Paragraph>
        </ContentContainer>

        <ContentContainer>
          <Subheader>History</Subheader>
          <Paragraph>
            EMS technology was first developed in the 1960s. Full-Body EMS
            developed in Europe in the early 2000s, with the Klitschko brothers
            (boxing) and players from FC Bayern Munich and the German National
            Soccer using the technology to gain a competitive advantage in
            athletic competition. Soon, teams like Real Madrid were following
            and Full-Body EMS became a staple training method for soccer
            players. Usain Bolt famously incorporated Full-Body EMS into his
            training regime while recovering from an injury ahead of setting
            multiple world records at the 2008 Olympic Games. Around 2010,
            Full-Body EMS went mainstream in Europe and quickly expanded across
            the globe.
          </Paragraph>

          <Paragraph>
            Today there are &gt;13,000 personal training studios globally
            specializing in Full-Body EMS. It is loved by countless athletes
            (Rafael Nadal, Roger Federer,&nbsp;
            <ExternalLink
              href="https://talksport.com/sport/boxing/740531/mike-tyson-electric-shock-muscle-therapy-roy-jones-jr-comeback-exhibition-fight/"
              target="_blank"
            >
              Mike Tyson
            </ExternalLink>
            ), celebrities (
            <ExternalLink
              href="https://www.malibumag.com/fitness-1/the-ultimate-fitness-trend"
              target="_blank"
            >
              Madonna
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://www.mensjournal.com/health-fitness/how-spiderman-gets-superhero-fit-w206061/"
              target="_blank"
            >
              Tom Holland
            </ExternalLink>
            ), and models (
            <ExternalLink
              href="https://www.scmp.com/lifestyle/health-wellness/article/2182014/electronic-muscle-stimulation-how-victorias-secret-angels"
              target="_blank"
            >
              Elsa Hosk
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://www.youbeauty.com/fitness/victorias-secret-supermodel-actually-stays-fit/"
              target="_blank"
            >
              Lindsay Ellingson
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://www.scmp.com/lifestyle/health-wellness/article/2182014/electronic-muscle-stimulation-how-victorias-secret-angels"
              target="_blank"
            >
              Alessandra Ambrosio
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://www.scmp.com/lifestyle/health-wellness/article/2182014/electronic-muscle-stimulation-how-victorias-secret-angels"
              target="_blank"
            >
              Ashley Graham
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://www.malibumag.com/fitness-1/the-ultimate-fitness-trend"
              target="_blank"
            >
              Heidi Klum
            </ExternalLink>
            ,&nbsp;
            <ExternalLink
              href="https://youtu.be/pds4FXW1imA?t=187"
              target="_blank"
            >
              Romee Strijd
            </ExternalLink>
            ).
          </Paragraph>

          <Paragraph>
            In 1999, the U.S. FDA began regulating EMS devices (a category they
            refer to as powered muscle stimulators) to ensure quality and user
            safety. The U.S.-specific clearance process is quite involved and
            complicated, and the major manufacturers of Full-Body EMS equipment
            were so busy expanding in the rest of the world that they only began
            gaining FDA clearance recently.
          </Paragraph>
          <Paragraph>
            Traditional Full-Body EMS equipment is expensive ($15k-20k) and
            complicated to operate. As a result, it has historically been
            limited to personal trainers, celebrities and professional athletes.
            The general public only had access to Full-Body EMS through very
            specialized personal trainers and studios. Katalyst set out to
            change this. Seeing a hunger for premium, at-home fitness products,
            we reinvented Full-Body EMS from the ground up and created the first
            FDA cleared, dynamic, immersive and content-driven platform
            accessible directly to consumers in the United States.
          </Paragraph>
        </ContentContainer>
      </Wrapper>
    </WhatIsEMSPageContainer>
  </>
)

export default WhatIsEMSPage
